import type { ErrorCode } from "./errors";
import type { Keyset, Paginated } from "./envelope";
import type { RoleCode } from "./auth";

/**
 * Merchandising (migration 0047): the portal's official store — products,
 * shopper accounts, orders, the sandbox payment gateway — and the GMS desk
 * that runs it. Wire shapes only (snake_case).
 *
 *   S1  The server prices every order: the basket sends variant ids and
 *       quantities, never a price. Totals are computed in the order statement.
 *   S2  Stock is reserved in ONE guarded UPDATE when the order is placed and
 *       given back when it is cancelled or its payment hold runs out.
 *   S3  Order status = SHOP_ORDER_TRANSITIONS, enforced by the trigger
 *       `fn_shop_order_status_guard` (a unit test holds them equal). Only the
 *       payment path moves an order to PAID.
 *   S4  Payments go through a provider interface. v1 has one provider, the
 *       SANDBOX simulator: it accepts only the test cards below, never a real
 *       card, and stores brand + last four digits only.
 *   S5  Shoppers are NOT staff logins: `shop_customers`, their own sessions,
 *       an access token for its own audience. A staff token never opens a
 *       shopper route, and a shopper token never opens a staff route.
 *   S6  The GMS desk is SUPER_ADMIN, plus the ADMIN logins on the merch staff
 *       list (`merch_staff`), decided in SQL on the ACTIVE role.
 *
 * Enum members are VALUES, never keys (`ok()` lower-cases all-caps keys), so
 * every count is `{ status, count }[]`.
 */

// ── Who runs the desk ───────────────────────────────────────────────────────

/** Active roles that can reach the desk at all. ADMIN also needs a `merch_staff` row. */
export const MERCH_ROLES = ["SUPER_ADMIN", "ADMIN"] as const satisfies readonly RoleCode[];
/** Only this role edits the merch staff list. */
export const MERCH_STAFF_EDITOR_ROLE = "SUPER_ADMIN" as const satisfies RoleCode;

// ── Catalogue vocabulary ────────────────────────────────────────────────────

export const SHOP_CURRENCY = "MYR" as const;

export const SHOP_CATEGORIES = ["Apparel", "Headwear", "Fan Gear", "Bags", "Drinkware", "Tuah", "Collectibles", "Home"] as const;
export type ShopCategory = (typeof SHOP_CATEGORIES)[number];

export const SHOP_COLLECTIONS = ["team-malaysia", "tuah", "batik", "collect", "para", "core"] as const;
export type ShopCollection = (typeof SHOP_COLLECTIONS)[number];

export const SHOP_TAGS = ["new", "bestseller", "sale", "limited"] as const;
export type ShopTag = (typeof SHOP_TAGS)[number];

/** The drawn silhouette the portal shows when a product has no photo (`shots.js`). */
export const SHOP_PRODUCT_KINDS = ["tee", "jersey", "hoodie", "cap", "scarf", "flag", "tote", "bottle", "plush", "pin", "keyring", "towel", "poster"] as const;
export type ShopProductKind = (typeof SHOP_PRODUCT_KINDS)[number];

/** Every colour a variant may be sold in, with its swatch (the brand palette). */
export const SHOP_COLOURS = {
	navy: "#2e3192",
	red: "#ed1c24",
	yellow: "#ffcb05",
	gold: "#b8a25b",
	white: "#ffffff",
	ink: "#12143f",
	sand: "#e8e3d3",
} as const;
export type ShopColour = keyof typeof SHOP_COLOURS;
export const SHOP_COLOUR_CODES = Object.keys(SHOP_COLOURS) as [ShopColour, ...ShopColour[]];

export const SHOP_SIZES = ["XS", "S", "M", "L", "XL", "2XL", "One size"] as const;
export type ShopSize = (typeof SHOP_SIZES)[number];

// ── Delivery ────────────────────────────────────────────────────────────────

/**
 * How an order reaches the shopper. Prices in ringgit. `free_from` = the goods
 * subtotal at which this method costs nothing. COLLECT needs a store and no
 * address; ASEAN ships to the ASEAN countries other than Malaysia.
 */
export const SHOP_DELIVERY_METHODS = ["STANDARD", "NEXT_DAY", "COLLECT", "ASEAN"] as const;
export type ShopDeliveryMethod = (typeof SHOP_DELIVERY_METHODS)[number];

export const SHOP_DELIVERY = {
	STANDARD: { price: 12, free_from: 250, needs_address: true, label: "Standard — Malaysia", detail: "2–4 working days, tracked" },
	NEXT_DAY: { price: 25, free_from: null, needs_address: true, label: "Next working day", detail: "Order before 14:00 MYT" },
	COLLECT: { price: 0, free_from: null, needs_address: false, label: "Collect at a Games store", detail: "Bukit Jalil, Axiata, MITEC — from 1 Sep 2027" },
	ASEAN: { price: 35, free_from: null, needs_address: true, label: "ASEAN shipping", detail: "5–9 working days, tracked" },
} as const satisfies Record<ShopDeliveryMethod, { price: number; free_from: number | null; needs_address: boolean; label: string; detail: string }>;

export const SHOP_COLLECT_STORES = ["BUKIT_JALIL", "AXIATA", "MITEC"] as const;
export type ShopCollectStore = (typeof SHOP_COLLECT_STORES)[number];
export const SHOP_COLLECT_STORE_NAMES = { BUKIT_JALIL: "Bukit Jalil", AXIATA: "Axiata Arena", MITEC: "MITEC" } as const satisfies Record<ShopCollectStore, string>;

/** Malaysia's states and federal territories, for STANDARD / NEXT_DAY addresses. */
export const MY_STATES = [
	"Johor",
	"Kedah",
	"Kelantan",
	"Kuala Lumpur",
	"Labuan",
	"Melaka",
	"Negeri Sembilan",
	"Pahang",
	"Penang",
	"Perak",
	"Perlis",
	"Putrajaya",
	"Sabah",
	"Sarawak",
	"Selangor",
	"Terengganu",
] as const;

/** ISO 3166-1 alpha-2 of the ASEAN countries an address may be in (MY for the domestic methods). */
export const SHOP_SHIP_COUNTRIES = ["MY", "BN", "KH", "ID", "LA", "MM", "PH", "SG", "TH", "TL", "VN"] as const;
export type ShopShipCountry = (typeof SHOP_SHIP_COUNTRIES)[number];

/** What one delivery costs for a goods subtotal (ringgit, 2 dp). The SQL in the order statement is the same rule. */
export function shopDeliveryFee(method: ShopDeliveryMethod, goodsSubtotal: number): number {
	const d = SHOP_DELIVERY[method];
	return d.free_from !== null && goodsSubtotal >= d.free_from ? 0 : d.price;
}

// ── Promotions ──────────────────────────────────────────────────────────────

export const SHOP_PROMO_KINDS = ["PERCENT", "FIXED"] as const;
export type ShopPromoKind = (typeof SHOP_PROMO_KINDS)[number];

/**
 * The discount a promotion gives on a goods subtotal (ringgit, 2 dp, never
 * more than the subtotal). PERCENT `value` is a whole percent (10 = 10 %).
 * The order statement computes the same in SQL; a unit test holds them equal.
 */
export function shopPromoDiscount(kind: ShopPromoKind, value: number, goodsSubtotal: number): number {
	const raw = kind === "PERCENT" ? Math.round(goodsSubtotal * value) / 100 : value;
	return Math.min(Math.round(raw * 100) / 100, goodsSubtotal);
}

/**
 * Why a promotion code was not applied (the quote's `promo_refusal`):
 *   unknown       no such code (or inactive)
 *   not_started   its window has not opened
 *   ended         its window has closed
 *   min_subtotal  the goods subtotal is below its minimum
 *   used_up       it reached its maximum number of orders
 */
export const SHOP_PROMO_REFUSALS = ["unknown", "not_started", "ended", "min_subtotal", "used_up"] as const;
export type ShopPromoRefusal = (typeof SHOP_PROMO_REFUSALS)[number];

// ── Orders ──────────────────────────────────────────────────────────────────

/** `shop_orders.status` (`ck_shop_orders_status`). A new order is PENDING_PAYMENT. */
export const SHOP_ORDER_STATUSES = ["PENDING_PAYMENT", "PAID", "PACKING", "SHIPPED", "DELIVERED", "CANCELLED", "REFUNDED"] as const;
export type ShopOrderStatus = (typeof SHOP_ORDER_STATUSES)[number];

/** The status an order is created in (`fn_shop_order_status_guard`, INSERT). */
export const SHOP_ORDER_INITIAL_STATUSES = ["PENDING_PAYMENT"] as const satisfies readonly ShopOrderStatus[];

/**
 * The status graph (S3). `fn_shop_order_status_guard` (0047) refuses any other
 * move with 23514 `ck_shop_orders_status_transition` (→ 409
 * INVALID_STATUS_TRANSITION), psql included; tests/unit/shop-order-guard.test.ts
 * holds the two equal. Change both in one migration.
 *
 *   PENDING_PAYMENT → PAID       the payment path only (tx-local app.shop_payment)
 *   PENDING_PAYMENT → CANCELLED  the shopper, the desk, or the hold running out (stock back)
 *   PAID / PACKING  → REFUNDED   the desk: cancelled after payment, money back
 *   DELIVERED       → REFUNDED   the desk: a return
 *   PAID → PACKING → SHIPPED → DELIVERED   the desk (SHIPPED needs courier + tracking no., or a COLLECT order)
 */
export const SHOP_ORDER_TRANSITIONS = {
	PENDING_PAYMENT: ["PAID", "CANCELLED"],
	PAID: ["PACKING", "REFUNDED"],
	PACKING: ["SHIPPED", "REFUNDED"],
	SHIPPED: ["DELIVERED"],
	DELIVERED: ["REFUNDED"],
	CANCELLED: [],
	REFUNDED: [],
} as const satisfies Record<ShopOrderStatus, readonly ShopOrderStatus[]>;

export const shopOrderTransitionPairs = (): Array<[ShopOrderStatus, ShopOrderStatus]> =>
	SHOP_ORDER_STATUSES.flatMap((from) =>
		(SHOP_ORDER_TRANSITIONS[from] as readonly ShopOrderStatus[]).map((to): [ShopOrderStatus, ShopOrderStatus] => [from, to]),
	);
export const shopOrderCanMove = (from: ShopOrderStatus, to: ShopOrderStatus): boolean =>
	(SHOP_ORDER_TRANSITIONS[from] as readonly ShopOrderStatus[]).includes(to);

/** The moves the desk takes by hand (PAID is the payment path's alone). */
export const SHOP_ORDER_DESK_TARGETS = ["PACKING", "SHIPPED", "DELIVERED", "CANCELLED", "REFUNDED"] as const satisfies readonly ShopOrderStatus[];
export type ShopOrderDeskTarget = (typeof SHOP_ORDER_DESK_TARGETS)[number];
/** Moves the desk may take for many orders at once. */
export const SHOP_ORDER_BULK_TARGETS = ["PACKING", "DELIVERED"] as const satisfies readonly ShopOrderDeskTarget[];
export type ShopOrderBulkTarget = (typeof SHOP_ORDER_BULK_TARGETS)[number];
/** The desk's moves that need a note (the audit headline and the shopper's timeline). */
export const SHOP_NOTE_REQUIRED = ["CANCELLED", "REFUNDED"] as const satisfies readonly ShopOrderDeskTarget[];
/** Statuses that still hold reserved stock and wait on a payment. */
export const SHOP_OPEN_PAYMENT_STATUSES = ["PENDING_PAYMENT"] as const satisfies readonly ShopOrderStatus[];
/** Paid and not yet out of the warehouse: the desk's "to do". */
export const SHOP_TO_FULFIL_STATUSES = ["PAID", "PACKING"] as const satisfies readonly ShopOrderStatus[];

/** `MERCH-000123`: how an order number is printed everywhere. Search also takes the bare number. */
export const formatShopOrderNo = (orderNo: number): string => `MERCH-${String(orderNo).padStart(6, "0")}`;
export function parseShopOrderNo(q: string): number | null {
	const m = /^\s*(?:MERCH-?)?0*(\d{1,9})\s*$/i.exec(q);
	return m ? Number(m[1]) : null;
}

/**
 * Why a bulk move left an order out:
 *   not_found           no such order
 *   invalid_transition  its status has no edge to the target
 *   changed             it changed between the read and the write (retry)
 */
export const SHOP_BULK_SKIP_REASONS = ["not_found", "invalid_transition", "changed"] as const;
export type ShopBulkSkipReason = (typeof SHOP_BULK_SKIP_REASONS)[number];

// ── Payments (the sandbox gateway) ──────────────────────────────────────────

export const SHOP_PAYMENT_METHODS = ["FPX", "CARD", "EWALLET", "DUITNOW_QR"] as const;
export type ShopPaymentMethod = (typeof SHOP_PAYMENT_METHODS)[number];

export const SHOP_PAYMENT_STATUSES = ["PENDING", "SUCCEEDED", "FAILED", "EXPIRED", "REFUNDED"] as const;
export type ShopPaymentStatus = (typeof SHOP_PAYMENT_STATUSES)[number];

/**
 * A payment attempt's graph, enforced by `fn_shop_payment_status_guard` (0047,
 * 23514 `ck_shop_payments_status_transition`); the unit test holds them equal.
 * A settled attempt never changes again, bar a refund of a success.
 */
export const SHOP_PAYMENT_TRANSITIONS = {
	PENDING: ["SUCCEEDED", "FAILED", "EXPIRED"],
	SUCCEEDED: ["REFUNDED"],
	FAILED: [],
	EXPIRED: [],
	REFUNDED: [],
} as const satisfies Record<ShopPaymentStatus, readonly ShopPaymentStatus[]>;

export const shopPaymentTransitionPairs = (): Array<[ShopPaymentStatus, ShopPaymentStatus]> =>
	SHOP_PAYMENT_STATUSES.flatMap((from) =>
		(SHOP_PAYMENT_TRANSITIONS[from] as readonly ShopPaymentStatus[]).map((to): [ShopPaymentStatus, ShopPaymentStatus] => [from, to]),
	);

/** How long a shopper has on the sandbox page before the attempt expires. */
export const SHOP_PAYMENT_ATTEMPT_MINUTES = 10;

/** Which gateway took a payment. v1: the sandbox only; a real gateway is a new value + a provider module. */
export const SHOP_PAYMENT_PROVIDERS = ["SANDBOX"] as const;
export type ShopPaymentProvider = (typeof SHOP_PAYMENT_PROVIDERS)[number];

/** FPX banks the sandbox lists (names as text, no logos; the sandbox never asks for a bank login). */
export const SHOP_FPX_BANKS = {
	MBB: "Maybank2u",
	CIMB: "CIMB Clicks",
	PBB: "Public Bank",
	RHB: "RHB Now",
	HLB: "Hong Leong Connect",
	AMB: "AmOnline",
	BIMB: "Bank Islam",
	BSN: "BSN",
	UOB: "UOB",
	OCBC: "OCBC",
} as const;
export type ShopFpxBank = keyof typeof SHOP_FPX_BANKS;
export const SHOP_FPX_BANK_CODES = Object.keys(SHOP_FPX_BANKS) as [ShopFpxBank, ...ShopFpxBank[]];

export const SHOP_EWALLETS = { TNG: "Touch 'n Go eWallet", GRABPAY: "GrabPay", BOOST: "Boost", SHOPEEPAY: "ShopeePay" } as const;
export type ShopEwallet = keyof typeof SHOP_EWALLETS;
export const SHOP_EWALLET_CODES = Object.keys(SHOP_EWALLETS) as [ShopEwallet, ...ShopEwallet[]];

/**
 * The ONLY card numbers the sandbox accepts (the widely published test
 * numbers). Anything else is refused as `not_test_card` before it is looked
 * at further, so a real card is never processed — and only the brand and the
 * last four digits are ever stored.
 *   approve        paid
 *   decline        declined by the issuer
 *   insufficient   insufficient funds
 *   three_ds       asks for a one-time code (SHOP_SANDBOX_OTP), then pays
 *   expired_card   the card has expired
 */
export const SHOP_TEST_CARDS = {
	"4242424242424242": { brand: "VISA", behaviour: "approve" },
	"5555555555554444": { brand: "MASTERCARD", behaviour: "approve" },
	"4000000000000002": { brand: "VISA", behaviour: "decline" },
	"4000000000009995": { brand: "VISA", behaviour: "insufficient" },
	"4000002760003184": { brand: "VISA", behaviour: "three_ds" },
	"4000000000000069": { brand: "VISA", behaviour: "expired_card" },
} as const;
export type ShopTestCardBehaviour = (typeof SHOP_TEST_CARDS)[keyof typeof SHOP_TEST_CARDS]["behaviour"];
export const SHOP_CARD_BRANDS = ["VISA", "MASTERCARD"] as const;
export type ShopCardBrand = (typeof SHOP_CARD_BRANDS)[number];
/** The 3-D Secure code the sandbox expects. */
export const SHOP_SANDBOX_OTP = "123456";

/** The test card behind a typed number (spaces and dashes ignored), or null — not a test card. */
export function shopTestCard(cardNumber: string): { brand: ShopCardBrand; behaviour: ShopTestCardBehaviour; last4: string } | null {
	const digits = cardNumber.replace(/[\s-]/g, "");
	const hit = (SHOP_TEST_CARDS as Record<string, { brand: ShopCardBrand; behaviour: ShopTestCardBehaviour }>)[digits];
	return hit ? { ...hit, last4: digits.slice(-4) } : null;
}

/**
 * What a shopper does on the sandbox page (`POST /public/shop/sandbox/:id`):
 *   APPROVE   FPX / e-wallet / QR: approve in the "bank" or "app"
 *   DECLINE   FPX / e-wallet / QR: decline
 *   TIMEOUT   FPX / e-wallet / QR: walk away (the attempt expires)
 *   CARD      submit card details (test numbers only)
 *   OTP       answer a 3-D Secure challenge
 */
export const SHOP_SANDBOX_ACTIONS = ["APPROVE", "DECLINE", "TIMEOUT", "CARD", "OTP"] as const;
export type ShopSandboxAction = (typeof SHOP_SANDBOX_ACTIONS)[number];

/**
 * Why a payment attempt failed (`shop_payments.failure_reason`):
 *   declined          the bank / issuer / wallet said no
 *   insufficient      insufficient funds
 *   expired_card      the card has expired
 *   otp_failed        three wrong 3-D Secure codes
 *   timed_out         nobody answered in time
 *   not_test_card     a number that is not a sandbox test card (never processed)
 *   order_closed      the order was no longer waiting for payment
 */
export const SHOP_PAYMENT_FAILURES = ["declined", "insufficient", "expired_card", "otp_failed", "timed_out", "not_test_card", "order_closed"] as const;
export type ShopPaymentFailure = (typeof SHOP_PAYMENT_FAILURES)[number];

// ── Outbox (simulated email) ────────────────────────────────────────────────

export const SHOP_EMAIL_TEMPLATES = [
	// VERIFY_EMAIL / ACCOUNT_EXISTS are no longer written (no email confirmation since 2026-11-02); kept for old rows and the DB CHECK.
	"VERIFY_EMAIL",
	"ACCOUNT_EXISTS",
	"RESET_PASSWORD",
	"ORDER_PLACED",
	"ORDER_PAID",
	"ORDER_SHIPPED",
	"ORDER_READY_FOR_COLLECTION",
	"ORDER_DELIVERED",
	"ORDER_CANCELLED",
	"ORDER_REFUNDED",
] as const;
export type ShopEmailTemplate = (typeof SHOP_EMAIL_TEMPLATES)[number];

/** What a one-time link is for (`shop_customer_tokens.purpose`). VERIFY_EMAIL is no longer written (2026-11-02); kept for old rows and the DB CHECK. */
export const SHOP_TOKEN_PURPOSES = ["VERIFY_EMAIL", "RESET_PASSWORD"] as const;
export type ShopTokenPurpose = (typeof SHOP_TOKEN_PURPOSES)[number];

export const SHOP_CUSTOMER_STATUSES = ["ACTIVE", "DISABLED"] as const;
export type ShopCustomerStatus = (typeof SHOP_CUSTOMER_STATUSES)[number];

// ── Limits ──────────────────────────────────────────────────────────────────

/** Hard cap on products: the catalogue is one cached snapshot (the documented exception to "every list is paginated"). */
export const SHOP_PRODUCTS_MAX = 500;
export const SHOP_VARIANTS_PER_PRODUCT_MAX = 60;
export const SHOP_IMAGES_PER_PRODUCT_MAX = 12;
export const SHOP_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
export const SHOP_IMAGE_MAX_EDGE = 1600;
export const SHOP_IMAGE_CONTENT_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
export type ShopImageContentType = (typeof SHOP_IMAGE_CONTENT_TYPES)[number];
/** A shot shipped with the portal (`apps/web-portal/public/seagames/img/shop/`), referenced by path instead of uploaded. */
export const SHOP_STATIC_IMAGE_PATTERN = /^\/seagames\/img\/shop\/[a-z0-9-]+\.webp$/;
export const SHOP_SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;
export const SHOP_SKU_PATTERN = /^[A-Z0-9]+(-[A-Z0-9]+)*$/;
export const SHOP_PROMO_CODE_PATTERN = /^[A-Z0-9]+$/;
export const SHOP_SLUG_MAX = 60;
export const SHOP_NAME_MAX = 120;
export const SHOP_DESCRIPTION_MAX = 4000;
export const SHOP_SKU_MAX = 60;
export const SHOP_PRICE_MAX = 100_000;
export const SHOP_STOCK_MAX = 1_000_000;
/** Lines on one order, and units of one line. */
export const SHOP_ORDER_LINES_MAX = 30;
export const SHOP_LINE_QTY_MAX = 10;
/** Unpaid orders one shopper may hold at once (each holds stock): a basket cannot be used to hoard the shelf. */
export const SHOP_OPEN_ORDERS_MAX = 3;
export const SHOP_PROMO_CODE_MAX = 30;
export const SHOP_PROMO_PERCENT_MAX = 90;
export const SHOP_NOTE_MAX = 500;
export const SHOP_COURIER_MAX = 60;
export const SHOP_TRACKING_MAX = 60;
export const SHOP_BULK_MAX = 100;
export const SHOP_SEARCH_MAX = 100;
export const SHOP_CURSOR_MAX = 300;
export const SHOP_ADDRESSES_MAX = 10;
export const SHOP_EMAIL_MAX = 254;
export const SHOP_PERSON_NAME_MAX = 120;
export const SHOP_PHONE_MAX = 30;
export const SHOP_ADDRESS_LINE_MAX = 200;
export const SHOP_CITY_MAX = 80;
export const SHOP_POSTCODE_MAX = 12;
export const SHOP_STATE_MAX = 80;
export const SHOP_PASSWORD_MIN = 10;
export const SHOP_PASSWORD_MAX = 128;
/** Card fields reach the sandbox endpoint only: number, MM/YY, CVC. */
export const SHOP_CARD_NUMBER_MAX = 23;
export const SHOP_OTP_ATTEMPTS_MAX = 3;

// ── Wire shapes: catalogue (public) ─────────────────────────────────────────

export interface ShopVariant {
	id: string;
	colour: ShopColour;
	size: ShopSize;
	sku: string;
	/** Units that can be bought now (stock is reserved as orders are placed). */
	stock: number;
}

export interface ShopImage {
	id: string;
	/** The colourway this shot shows, or null for every colourway. */
	colour: ShopColour | null;
	url: string;
}

export interface ShopProduct {
	id: string;
	slug: string;
	name: string;
	description: string | null;
	category: ShopCategory;
	collection: ShopCollection;
	tags: ShopTag[];
	kind: ShopProductKind;
	/** Ringgit, 2 dp, as a number. */
	price: number;
	was_price: number | null;
	colours: ShopColour[];
	sizes: ShopSize[];
	variants: ShopVariant[];
	images: ShopImage[];
	/** Σ variant stock. */
	stock: number;
}

/** `GET /public/shop/catalogue`: every ACTIVE product, one cached snapshot. */
export interface ShopCatalogue {
	currency: typeof SHOP_CURRENCY;
	products: ShopProduct[];
	built_at: string;
}

// ── Wire shapes: shopper ────────────────────────────────────────────────────

export interface ShopCustomerMe {
	id: string;
	email: string;
	name: string;
	phone: string | null;
	created_at: string;
}

export interface ShopSession {
	access_token: string;
	expires_in: number;
	refresh_token: string;
	customer: ShopCustomerMe;
}

export interface ShopRefreshed {
	access_token: string;
	expires_in: number;
	/** Absent in the grace case: keep the refresh token you have. */
	refresh_token?: string;
}

/** Every sign-up, forgot-password and resend answers this — whatever the address (enumeration-safe). */
export interface ShopCheckEmail {
	sent: true;
	/** Dev / test only (`SHOP_OUTBOX_SHOW_LINKS`): the sandbox email, to open without a mailbox. Null in production. */
	sandbox_email_id: string | null;
}

export interface ShopAddress {
	id: string;
	label: string | null;
	recipient: string;
	phone: string;
	line1: string;
	line2: string | null;
	city: string;
	postcode: string;
	state: string;
	country: ShopShipCountry;
	is_default: boolean;
}

export interface ShopOrderLine {
	variant_id: string;
	product_slug: string;
	product_name: string;
	colour: ShopColour;
	size: ShopSize;
	sku: string;
	unit_price: number;
	qty: number;
	line_total: number;
	image_url: string | null;
}

/** The order's delivery, as captured when it was placed (a later address-book edit never changes it). */
export interface ShopOrderDelivery {
	method: ShopDeliveryMethod;
	collect_store: ShopCollectStore | null;
	recipient: string | null;
	phone: string | null;
	line1: string | null;
	line2: string | null;
	city: string | null;
	postcode: string | null;
	state: string | null;
	country: ShopShipCountry | null;
}

export interface ShopPaymentView {
	id: string;
	method: ShopPaymentMethod;
	provider: ShopPaymentProvider;
	status: ShopPaymentStatus;
	amount: number;
	bank: string | null;
	card_brand: ShopCardBrand | null;
	card_last4: string | null;
	failure_reason: ShopPaymentFailure | null;
	provider_ref: string | null;
	created_at: string;
	settled_at: string | null;
}

/** One step of the shopper's tracking timeline (read from the order's status stamps). */
export interface ShopTimelineStep {
	status: ShopOrderStatus;
	at: string | null;
}

export interface ShopOrderTotals {
	subtotal: number;
	discount: number;
	delivery_fee: number;
	total: number;
}

export interface ShopOrderSummary extends ShopOrderTotals {
	id: string;
	order_no: number;
	order_ref: string;
	status: ShopOrderStatus;
	items: number;
	created_at: string;
	payment_due_at: string | null;
}

export interface ShopOrder extends ShopOrderSummary {
	promo_code: string | null;
	delivery: ShopOrderDelivery;
	courier: string | null;
	tracking_no: string | null;
	status_note: string | null;
	lines: ShopOrderLine[];
	payments: ShopPaymentView[];
	timeline: ShopTimelineStep[];
	/** The shopper may still pay / cancel it. */
	can_pay: boolean;
	can_cancel: boolean;
}

/**
 * `POST /public/shop/orders/quote` — the basket priced by the server, the
 * same statement as placing it, nothing written. A line short of stock is
 * listed in `short` with what is left.
 */
export interface ShopQuote extends ShopOrderTotals {
	lines: ShopOrderLine[];
	short: Array<{ variant_id: string; available: number }>;
	unknown_variant_ids: string[];
	promo_code: string | null;
	promo_refusal: ShopPromoRefusal | null;
}

/** `POST /public/shop/orders/:id/payments`: where to send the shopper (the sandbox page). */
export interface ShopPaymentStart {
	payment_id: string;
	redirect_url: string;
	expires_at: string;
}

/** `GET /public/shop/sandbox/:id`: what the sandbox page shows. */
export interface ShopSandboxView {
	payment_id: string;
	method: ShopPaymentMethod;
	status: ShopPaymentStatus;
	amount: number;
	currency: typeof SHOP_CURRENCY;
	merchant: string;
	order_ref: string;
	bank: string | null;
	/** True after a 3-D Secure card was accepted: the page asks for the code. */
	awaiting_otp: boolean;
	failure_reason: ShopPaymentFailure | null;
	expires_at: string;
	return_url: string;
}

/** `POST /public/shop/sandbox/:id`'s answer. */
export interface ShopSandboxResult {
	status: ShopPaymentStatus;
	awaiting_otp: boolean;
	failure_reason: ShopPaymentFailure | null;
	return_url: string;
}

export interface ShopSandboxEmail {
	id: string;
	to: string;
	template: ShopEmailTemplate;
	subject: string;
	body: string;
	/** Absent when links are hidden (production). */
	links: Array<{ label: string; url: string }>;
	created_at: string;
}

// ── Wire shapes: the GMS desk ───────────────────────────────────────────────

export interface MerchOrderRow extends ShopOrderSummary {
	customer_id: string;
	customer_name: string;
	customer_email: string;
	delivery_method: ShopDeliveryMethod;
	needs_refund: boolean;
	last_payment_status: ShopPaymentStatus | null;
	last_payment_method: ShopPaymentMethod | null;
	tracking_no: string | null;
	version: number;
}

export interface MerchOrderDetail extends ShopOrder {
	customer: { id: string; name: string; email: string; phone: string | null; status: ShopCustomerStatus };
	needs_refund: boolean;
	version: number;
	/** The desk moves this order can take now (computed from the same rule as the write). */
	next_statuses: ShopOrderDeskTarget[];
	restocked: boolean;
}

export interface MerchOrderList extends Keyset<MerchOrderRow> {
	/** First page only. */
	total: number | null;
}

/** `GET /merch/summary`: the desk's tiles, ONE aggregate. */
export interface MerchSummary {
	by_status: Array<{ status: ShopOrderStatus; count: number }>;
	needs_refund: number;
	revenue_paid: number;
	orders_today: number;
	low_stock_variants: number;
	customers: number;
}

export interface MerchProductRow {
	id: string;
	slug: string;
	name: string;
	category: ShopCategory;
	collection: ShopCollection;
	price: number;
	was_price: number | null;
	active: boolean;
	variants: number;
	stock: number;
	image_url: string | null;
	position: number;
	updated_at: string;
}

export interface MerchVariant extends ShopVariant {
	active: boolean;
}

export interface MerchImage extends ShopImage {
	position: number;
	/** A shot shipped with the portal (seeded) rather than one uploaded here. */
	is_static: boolean;
}

export interface MerchProduct {
	id: string;
	slug: string;
	name: string;
	description: string | null;
	category: ShopCategory;
	collection: ShopCollection;
	tags: ShopTag[];
	kind: ShopProductKind;
	price: number;
	was_price: number | null;
	active: boolean;
	position: number;
	variants: MerchVariant[];
	images: MerchImage[];
	version: number;
	created_at: string;
	updated_at: string;
}

export type MerchProductList = Paginated<MerchProductRow>;

export interface MerchPromo {
	id: string;
	code: string;
	kind: ShopPromoKind;
	value: number;
	min_subtotal: number;
	starts_at: string | null;
	ends_at: string | null;
	max_uses: number | null;
	uses: number;
	active: boolean;
	version: number;
}

export type MerchPromoList = Paginated<MerchPromo>;

export interface MerchCustomerRow {
	id: string;
	email: string;
	name: string;
	phone: string | null;
	status: ShopCustomerStatus;
	orders: number;
	spent: number;
	created_at: string;
	version: number;
}

export interface MerchCustomerList extends Keyset<MerchCustomerRow> {
	total: number | null;
}

export interface MerchOutboxRow {
	id: string;
	to: string;
	template: ShopEmailTemplate;
	subject: string;
	body: string;
	/** Redacted (empty) unless SHOP_OUTBOX_SHOW_LINKS is on. */
	links: Array<{ label: string; url: string }>;
	links_hidden: boolean;
	created_at: string;
}

export type MerchOutboxList = Keyset<MerchOutboxRow>;

export interface MerchStaffRow {
	user_id: string;
	username: string;
	full_name: string;
	email: string | null;
	status: string;
	added_at: string;
	added_by_name: string | null;
}

export type MerchStaffList = Paginated<MerchStaffRow>;

export interface MerchBulkResult {
	updated: string[];
	skipped: Array<{ id: string; reason: ShopBulkSkipReason }>;
}

// ── Errors (the codes this module adds) ─────────────────────────────────────

/** For the frontends' switch: every code merchandising can answer with. */
export const SHOP_ERROR_CODES = [
	"OUT_OF_STOCK",
	"SHOP_EMAIL_TAKEN",
	"ORDER_NOT_PAYABLE",
	"PAYMENT_NOT_PENDING",
	"SHIPMENT_DETAILS_REQUIRED",
	"MERCH_STAFF_NOT_ADMIN",
	"PRODUCT_LIMIT_REACHED",
	"PROMO_NOT_APPLICABLE",
] as const satisfies readonly ErrorCode[];
